<template>
    <Transition>
        <div v-if="error" class="alert alert-danger" role="alert">
            There was an error. Please try again.
        </div>
    </Transition>

    <div v-show="loading" class="spinnerLoaderContainer">
        <div v-if="loading" class="spinnerLoader mt-2 mx-auto"></div>
    </div>

    <!-- Phrases -->
    <div v-if="!loading && !items || items && items.length < 1" v-cloak>
        <!-- <p>You have no Text-to-Speech phrases yet.</p> -->
    </div>

    <div>
        <div class="datatable-container">
            <DataTable ref="dataTable" :value="items" :tableStyle="{ tableLayout: 'fixed', width: '100%' }" :lazy="true"
                :paginator="true" :rows="rows" :totalRecords="totalRecords" :first="(page - 1) * rows" :loading="false"
                @page="onPage" @sort="onSort" :globalFilterFields="['phrase_text', 'board.board_title']"
                v-model:filters="filters" scrollable scrollHeight="55vh" sortField="created_at" :sortOrder="-1"
                :virtualScrollerOptions="{ itemSize: 56 }" v-model:selection="selection" dataKey="id" :size="'small'"
                resizableColumns columnResizeMode="fit"
                paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                currentPageReportTemplate="{first} to {last} of {totalRecords}">

                <template #paginatorstart>
                </template>
                <template #paginatorend>
                </template>

                <template #empty>
                    <div class="text-center d-flex flex-column justify-content-center align-items-center py-4">
                        <div v-show="!loading">
                            <i class="fa fa-microphone-slash fa-3x mb-3"></i>
                            <h5>No TTS phrases found</h5>
                            <p class="mb-0">
                                <span v-if="filters.global.value || Object.values(filters).some(f => f.value)">
                                    Try adjusting your search filters or <a href="#" @click="clearFilters"
                                        class="text-primary">clear all filters</a>.
                                </span>
                                <span v-else>
                                    You haven't created any Text-to-Speech phrases yet.
                                </span>
                            </p>
                        </div>
                    </div>
                </template>

                <template #header>
                    <div class="w-100 text-right d-flex justify-content-end align-items-center">

                        <!-- Global search -->
                        <div class="input-group pb-1 flex-grow-1" style="max-width: 300px;">
                            <input type="text" class="form-control" :class="{ 'border-end-0': filters['global'].value }"
                                id="global_search_input" v-model="filters['global'].value" placeholder="Search..."
                                autocomplete="off" style="box-shadow: none;" />
                            <div class="input-group-append">
                                <button v-if="filters['global'].value" class="btn btn-outline-secondary border-start-0"
                                    type="button" v-on:click="filters['global'].value = null" v-tooltip="'Clear search'"
                                    style="border-radius: 0 0.25rem 0.25rem 0;" id="global_search_input_clear_button">
                                    <i class="fa fa-times"></i>
                                </button>
                            </div>
                        </div>

                        <Transition>
                            <button class="btn btn-danger text-white ms-1 mb-1"
                                v-bind:disabled="!selection || selection.length < 1" v-on:click="deleteSelection()">
                                <i class="fa fa-fw fa-trash me-1"></i>
                                <span class="d-none d-md-inline">Delete</span>
                                <span class="d-none d-lg-inline">
                                    ({{ selection && selection.length ? selection.length + ' selected' : '0 selected'
                                    }})
                                </span>
                            </button>
                        </Transition>

                        <button class="btn btn-primary text-white ms-1 mb-1" v-on:click="throttledLoad()"
                            v-bind:disabled="!canRefresh"
                            :style="!canRefresh ? 'background-color: #dee2e6; border-color: #dee2e6; cursor: not-allowed;' : ''"
                            v-tooltip="!canRefresh ? 'Please wait a moment before refreshing again' : ''">
                            <i v-if="!canRefresh" class="fa fa-fw fa-clock-rotate-left me-1"></i>
                            <i v-else class="fa fa-fw fa-refresh me-1"></i>
                            <span class="d-none d-md-inline">Refresh</span>
                        </button>

                        <button class="btn btn-outline-dark btn-no-hover ms-1 mb-1" type="button"
                            v-on:click="$refs.notificationPreference.handleClick()">
                            <NotificationPreference :explanation="false" ref="notificationPreference">
                            </NotificationPreference>
                        </button>

                        <!-- Live updates indicator -->
                        <div class="ms-2 mb-1 d-inline-flex align-items-center">
                            <div>
                                <web-sockets-indicator @reconnected="throttledLoad"></web-sockets-indicator>
                            </div>
                        </div>
                    </div>
                </template>

                <Column selectionMode="multiple" headerStyle="width: 35px;"></Column>

                <Column field="phrase_text" header="Phrase" sortable headerStyle="width: 260px;">
                    <template #body="slotProps">
                        <div v-if="slotProps.data.sound && slotProps.data.sound.link" class="tts-phrase-cell">
                            <sound-player class="soundPlayer" v-bind:sound="slotProps.data.sound"
                                v-bind:edit-enabled="false" v-bind:edit-sounds-enabled="false"
                                v-bind:download-enabled="false" v-bind:show-waveform="false" v-bind:auto-play="false"
                                v-bind:preload="true" v-on:download="download" v-on:show-modal=""
                                v-on:show-contextmenu="show_contextMenu" v-bind:volume="100"
                                v-bind:large-download-button="false"
                                v-bind:ref="'soundPlayer_' + slotProps.data.sound.id"
                                v-on:show-drop-target="showDropTarget = false"
                                v-bind:context-menu-enable-more-options="false" v-bind:hide-footer="true"
                                v-bind:id="'soundPlayer_' + slotProps.data.sound.id">
                            </sound-player>
                        </div>

                        <div class="my-tts-status user-select-none text-start tts-phrase-cell d-flex align-items-center"
                            v-else>
                            <span v-if="slotProps.data.flagged"
                                class="badge rounded-pill bg-danger p-2 w-100 d-inline-flex justify-content-center"
                                v-tooltip="'Automated content filtering flagged this phrase as potentially inappropriate'">
                                <i class="fa fa-fw fa-flag me-2"></i>Flagged
                            </span>

                            <span v-else-if="slotProps.data.reported"
                                class="badge rounded-pill bg-danger p-2 w-100 d-inline-flex justify-content-center"
                                v-tooltip="'Removed due to user complaints'">
                                <i class="fa fa-fw fa-flag me-2"></i>Reported
                            </span>

                            <span v-else-if="slotProps.data.failed_at"
                                class="btn btn-warning w-100 d-inline-flex align-items-center justify-content-between">
                                <i class="fa fa-fw fa-circle-exclamation me-2"></i>Failed
                                <button class="btn btn-info btn-xs text-white ms-2" v-on:click="retry(slotProps.data)">
                                    <i class="fa fa-fw fa-redo me-1"></i>Retry
                                </button>
                            </span>

                            <div v-else-if="!slotProps.data.started_at"
                                class="badge rounded-pill bg-primary p-2 text-center w-100 d-inline-flex justify-content-center">
                                <i class="fa fa-fw fa-hourglass-half me-2"></i>Queued
                            </div>

                            <span v-else-if="slotProps.data.started_at && !slotProps.data.completed_at"
                                class="badge rounded-pill bg-info p-2 w-100 d-inline-flex justify-content-center align-items-center">
                                <span class="spinner-border spinner-border-sm me-2" role="status"
                                    aria-hidden="true"></span>
                                Processing...
                            </span>

                            <span v-else-if="slotProps.data.completed_at">
                                <span v-if="slotProps.data.sound && !slotProps.data.uploaded_at"
                                    class="badge rounded-pill bg-info p-2 w-100 d-inline-flex justify-content-center align-items-center">
                                    <span class="spinner-border spinner-border-sm me-2" role="status"
                                        aria-hidden="true"></span>
                                    Uploading...
                                </span>

                                <span v-else
                                    class="badge rounded-pill bg-warning p-2 w-100 d-inline-flex flex-column align-items-center">
                                    <i class="fa fa-fw fa-circle-exclamation me-2"></i>Not found
                                    <br />
                                    <button class="btn btn-info mt-1" v-on:click="retry(slotProps.data)">
                                        <i class="fa fa-fw fa-redo me-1"></i>Retry
                                    </button>
                                </span>
                            </span>
                        </div>
                    </template>
                </Column>

                <Column header="" headerStyle="width: 80px;">
                    <template #body="slotProps">
                        <span
                            v-on:click="slotProps.data.sound && slotProps.data.sound.link ? download(slotProps.data.sound) : null"
                            v-tooltip.top="slotProps.data.sound && slotProps.data.sound.link ? 'Download your sound' : 'Sound is not ready yet'">
                            <button class="btn btn-danger text-white btn-icon ms-1 me-1"
                                v-bind:disabled="!(slotProps.data.sound && slotProps.data.sound.link)"
                                aria-label="Download sound">
                                <i class="fa fa-fw fa-download"></i>
                            </button>
                        </span>

                        <button class="btn btn-primary text-white btn-icon" v-on:click="edit(slotProps.data)"
                            :disabled="slotProps.data.reported">
                            <i class="fa fa-fw fa-pen-to-square"></i>
                        </button>
                    </template>
                </Column>

                <Column header="Voice" field="board.board_tts_title" sortable headerStyle="width: 250px" v-if="showVoiceColumn">
                    <template #body="slotProps">
                        <button class="btn btn-link p-0 text-start text-decoration-none d-flex align-items-center"
                            v-on:click="$emit('select-board', slotProps.data.board)" v-tooltip.top="'Use this voice'">
                            <img v-if="slotProps.data.board.board_tiny_icon_url_if_exists"
                                v-bind:src="slotProps.data.board.board_tiny_icon_url_if_exists" class="me-1 align-top"
                                width="20" height="20" v-bind:alt="slotProps.data.board.board_image_alt_text" />
                            {{ _.truncate(slotProps.data.board.board_tts_title, { length: 80, omission: '...' }) }}
                        </button>
                    </template>
                </Column>

                <Column header="Added" field="created_at" sortable headerStyle="width: 115px; text-overflow: ellipsis">
                    <template #body="slotProps">
                        {{ _.upperFirst(moment(slotProps.data.created_at).fromNow()) }}
                    </template>
                </Column>

                <template #footer>
                    <div class="d-flex justify-content-between align-items-center">
                        <!-- Left side - showing results info -->
                        <div class="small">
                            <span v-if="totalRecords > 0">
                                Showing {{ ((page - 1) * rows) + 1 }} to {{ Math.min(page * rows, totalRecords) }}
                                of {{ totalRecords }} results
                            </span>
                            <span v-else>No results found</span>
                        </div>

                        <!-- Right side - rows per page selector -->
                        <div class="d-flex align-items-center">
                            <Select id="rowsPerPageFooter" v-model="rows" :options="rowsPerPageOptions"
                                optionLabel="label" optionValue="value" @change="onRowsPerPageChange"
                                class="p-select-sm" style="width: 100px;" :disabled="loading" />
                            <label for="rowsPerPageFooter" class="ms-2 mb-0 small">items per page</label>
                        </div>
                    </div>
                </template>
            </DataTable>
        </div>
        <sound-downloader ref="soundDownloader"></sound-downloader>

        <div class="dropdown-menu dropdown-menu-sm shadow bg-white" id="context-menu">
            <a class="dropdown-item" v-if="selectedSound && selectedSound.link" v-bind:href="selectedSound.link">
                <span class="fas fa-fw fa-link me-2" aria-hidden="true"></span>Go to sound page
            </a>
            <hr />
            <button class="dropdown-item" v-if="contextMenuEnableDownload"
                v-on:click="triggerDownloadSound(selectedSound)">
                <span class="fas fa-fw fa-download me-2" aria-hidden="true"></span>Download
            </button>

            <template v-if="favorites && selectedSound && favorites.includes(selectedSound.id)">
                <button class="dropdown-item" v-on:click="addToFavorites(selectedSound)">
                    <span class="fas fa-fw fa-star me-2 favorite-star-color-active" aria-hidden="true"></span>Unfavorite
                </button>
            </template>
            <template v-else>
                <button class="dropdown-item" v-on:click="addToFavorites(selectedSound)">
                    <span class="fas fa-fw fa-star me-2" aria-hidden="true"></span>Favorite
                </button>
            </template>

            <template v-if="editEnabled">
                <button class="dropdown-item"
                    v-on:click="editSoundsEnabled = !editSoundsEnabled; controlsVisible = true;">
                    <template v-if="!editSoundsEnabled">
                        <span class="fas fa-fw fa-edit me-2" aria-hidden="true"></span>Edit
                    </template>
                    <template v-else>
                        <span class="fas fa-fw fa-xmark me-2" aria-hidden="true"></span>Stop Editing
                    </template>
                </button>
            </template>

            <button class="dropdown-item" v-on:click="report(selectedSound)">
                <span class="fas fa-fw fa-flag me-2" aria-hidden="true"></span>Report
            </button>
            <button class="dropdown-item" v-on:click="copyToClipboard(selectedSound.link)">
                <span class="fas fa-fw fa-copy me-2" aria-hidden="true"></span>Copy link
            </button>

            <span id="contextmenu-share1">
                <button class="dropdown-item" v-on:click="webShare(selectedSound)">
                    <span class="fas fa-fw fa-share me-2" aria-hidden="true"></span><span>Share</span>
                </button>
            </span>

            <div v-if="contextMenuEnableMoreOptions">
                <hr />
                <button class="dropdown-item" v-on:click="show_soundModal(selectedSound)">
                    <span class="fas fa-fw fa-bars me-2" aria-hidden="true"></span>More options...
                </button>
            </div>
        </div>
    </div>

    <div>
        <!-- tiny button like on the movie The Net that reveals the webpush buttons -->
        <button class="btn btn-link p-0 m-0 text-dark" style="position: absolute; right: 5px;"
            v-on:click="showWebpushButtons = !showWebpushButtons" v-tooltip="'Toggle Webpush Notification Buttons'">π
        </button>
    </div>

    <template v-if="showWebpushButtons">
        <h4>Webpush Subscription (Alpha, Under Development, Top Secret O M G you get ZERO treats for finding this and it
            probably doesn't work)</h4>
        <div class="mb-5">
            <button class="btn btn-primary" @click="requestWebPushPermission">
                Enable Notifications
            </button>

            <button class="btn btn-danger" @click="removeSubscription">
                Disable Notifications
            </button>

            <button class="btn btn-secondary" @click="requestTestNotification">
                Send Test Notification
            </button>
        </div>
    </template>
</template>

<script>
import axios from 'axios';
import _ from 'lodash';
import moment from 'moment';
import Swal from 'sweetalert2';
import {
    canUseWebPush,
    canRequestWebPush,
    requestWebPushPermission,
    removeSubscription,
    requestTestNotification,
} from '../notifications.js';

import {
    DataTable,
    Column,
    ColumnGroup,
    Row,
    Select,
    InputText,
    FloatLabel,
    InputGroup,
    InputGroupAddon,
    Tooltip
} from 'primevue';
import { FilterMatchMode } from '@primevue/core/api';
import AudioPlayer from './AudioPlayer.vue';
import SoundPlayer from './SoundPlayer.vue';
import SoundDownloader from './SoundDownloader.vue';
import NotificationPreference from './NotificationPreference.vue';

export default {
    components: {
        DataTable, Column, ColumnGroup, Row, Select, InputText, AudioPlayer, SoundPlayer, SoundDownloader, NotificationPreference
    },
    directives: {
        tooltip: Tooltip
    },
    props: {
        boardId: {
            type: [Number, String],
            required: false,
        },
        showVoiceColumn: {
            type: Boolean,
            default: true,
        },
    },
    data: function () {
        return {
            items: [],
            totalRecords: 0,
            page: 1, //Current page
            rows: 50, //Rows per page
            loading: false,

            //Default sort
            sortField: 'created_at',
            sortOrder: '-1',

            filters: this.getDefaultFilters(),

            rowsPerPageOptions: [
                { label: '10', value: 10 },
                { label: '25', value: 25 },
                { label: '50', value: 50 },
                { label: '250', value: 250 },
            ],

            canRefresh: true,
            canRefreshTimeout: null,
            moment: moment,
            _: _,
            selection: null,

            audio: false,
            subscribed: false,
            error: false,
            scrollAttempts: 0,

            abortController: null,

            showWebpushButtons: false,

            selectedSound: null,
            favorites: [],
            contextMenuEnableDownload: true,
            contextMenuEnableMoreOptions: false,
            editEnabled: false,
            editSoundsEnabled: false,
            controlsVisible: true,
            contextMenuDimensions: null,
        };
    },
    emits: [
        'edit-phrase',
        'select-board',
        'phrase-finished',
    ],
    created: function () {
        console.log("MyTTSPhrases component created");
    },
    mounted: function () {
        console.log("MyTTSPhrases component mounted");

        this.load().then(() => {
            this.subscribe();
        });

        this.loadFavorites();

        //Reload data every 30 seconds
        // setInterval(() => {
        //     this.load();
        // }, 30000);

        //Create audio player
        this.audio = new Audio();

        window.addEventListener('wheel', this.handleGlobalWheel, { passive: false });

        document.addEventListener('visibilitychange', this.reloadOnVisibilityChange);
        document.addEventListener('click', this.handleDocumentClick);
        document.addEventListener('keydown', this.handleContextMenuKeydown);
    },
    beforeUnmount: function () {
        console.log("MyTTSPhrases component beforeUnmount");

        window.removeEventListener('wheel', this.handleGlobalWheel);

        document.removeEventListener('visibilitychange', this.reloadOnVisibilityChange);
        document.removeEventListener('click', this.handleDocumentClick);
        document.removeEventListener('keydown', this.handleContextMenuKeydown);
    },
    unmounted: function () {
        console.log("MyTTSPhrases component unmounted");
    },
    computed: {
    },
    methods: {
        async load(page = this.page, perPage = this.rows) {
            this.loading = true;

            // Abort previous request if running
            if (this.abortController) {
                this.abortController.abort();
            }
            this.abortController = new AbortController();

            this.canRefresh = false;
            if (this.canRefreshTimeout) {
                clearTimeout(this.canRefreshTimeout);
            }

            try {
                const params = {
                    page,
                    per_page: perPage,
                };

                //Sorting
                if (this.sortField) {
                    params.sort = this.sortOrder === 1 ? this.sortField : `-${this.sortField}`;
                }

                //All filters including global filter
                Object.keys(this.filters).forEach(key => {
                    if (this.filters[key].value) {
                        params[`filter[${key}]`] = this.filters[key].value;
                    }
                });

                // If boardId prop is provided, filter by that board ID
                if (this.boardId) {
                    params['filter[board_id]'] = this.boardId;
                }

                const res = await axios.get('/api/v1/tts/me', {
                    params,
                    signal: this.abortController.signal,
                });

                this.items = res.data.data;
                this.totalRecords = res.data.total;
                this.page = res.data.current_page;
                this.rows = res.data.per_page;

                return res; //Return the promise for further handling if needed
            } catch (error) {
                if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED') {
                    console.log('Previous request aborted');
                } else {
                    // ...handle other errors...
                    this.error = true;
                }
            } finally {
                this.loading = false;

                //Re-enable refresh button after 5 seconds
                this.canRefreshTimeout = setTimeout(() => {
                    this.canRefresh = true;
                }, 5000);
            }
        },

        getDefaultFilters() {
            return {
                global: { value: null, matchMode: FilterMatchMode.CONTAINS },
                phrase_text: { value: null, matchMode: FilterMatchMode.CONTAINS },
                'board.board_tts_title': { value: null, matchMode: FilterMatchMode.CONTAINS },
            };
        },

        validateWebsocketData(data) {
            //Check if all required top-level keys are present for a complete item object
            const requiredKeys = [
                'id', 'created_at', 'updated_at', 'board_id', 'phrase_text',
                'started_at', 'completed_at', 'failed_at', 'flagged', 'sound_id', 'sound', 'board'
            ];

            for (const key of requiredKeys) {
                if (!(key in data)) {
                    console.log(`Missing key: ${key}`, data);
                    return false;
                }
            }

            //Check if board object exists and has required properties
            if (!data.board || typeof data.board !== 'object') {
                console.log('Missing or invalid board object', data);
                return false;
            }

            const requiredBoardKeys = [
                'id', 'board_title', 'tts_engine_id', 'board_tts_title',
                'board_tiny_icon_url_if_exists', 'board_image_alt_text',
                'board_image_url_if_exists', 'board_thumbnail_url_if_exists',
                'board_thumbnail_webp_url_if_exists', 'board_medium_thumbnail_webp_url_if_exists',
                'board_medium_thumbnail_url_if_exists', 'link',
                'board_is_tts',
            ];

            for (const key of requiredBoardKeys) {
                if (!(key in data.board)) {
                    console.log(`Missing board key: ${key}`, data.board);
                    return false;
                }
            }

            //Note: sound can be null, so we don't validate its structure here
            //as it's expected to be null for phrases that haven't completed yet

            return true;
        },

        reloadOnVisibilityChange(event) {
            if (document.visibilityState === 'visible') {
                console.log('Tab is now in focus, reloading data');
                this.load();
            }
        },

        onPage(event) {
            console.log('Page event:', event);
            this.page = event.page + 1; // PrimeVue uses 0-based indexing

            // Check if rows per page changed
            if (event.rows !== this.rows) {
                this.rows = event.rows;
                this.page = 1; // Reset to first page when changing rows per page
            }

            this.load().then(() => {
                this.scrollToTop();
            });
        },

        onSort(event) {
            this.sortField = event.sortField;
            this.sortOrder = event.sortOrder;
            this.page = 1;
            this.load().then(() => {
                this.scrollToTop();
            });
        },

        onRowsPerPageChange() {
            console.log('Rows per page changed to:', this.rows);
            this.page = 1; //Reset to first page when changing rows per page

            this.load().then(() => {
                this.scrollToTop();
            });
        },

        onRefresh() {
            this.load();
        },

        clearFilters() {
            this.filters = {
                global: { value: null, matchMode: FilterMatchMode.CONTAINS },
                phrase_text: { value: null, matchMode: FilterMatchMode.STARTS_WITH },
                'board.board_tts_title': { value: null, matchMode: FilterMatchMode.STARTS_WITH },
            };
            this.page = 1;
            this.load().then(() => {
                this.scrollToTop();
            });
        },

        scrollToTop() {
            this.$nextTick(() => {
                try {
                    if (this.$refs.dataTable && this.$refs.dataTable.getVirtualScrollerRef) {
                        const virtualScrollerRef = this.$refs.dataTable.getVirtualScrollerRef();
                        if (virtualScrollerRef && virtualScrollerRef.scrollToIndex) {
                            console.log('Scrolling to index 0 using virtual scroller API');
                            virtualScrollerRef.scrollToIndex(0);
                        }
                    }
                } catch (error) {
                    console.error('Error scrolling to top:', error);
                }
            });
        },

        show_contextMenu(sound, domEvent) {
            if (!sound) {
                return;
            }

            this.contextMenuEnableDownload = true;
            this.contextMenuEnableMoreOptions = false;
            this.selectedSound = sound;

            const contextMenu = document.getElementById('context-menu');
            if (!contextMenu) {
                return;
            }

            if (!this.contextMenuDimensions) {
                contextMenu.style.display = 'block';
                contextMenu.style.position = 'fixed';
                contextMenu.style.left = '-10000px';
                contextMenu.style.top = '-10000px';
                contextMenu.style.visibility = 'hidden';

                this.contextMenuDimensions = {
                    width: contextMenu.offsetWidth,
                    height: contextMenu.offsetHeight,
                };

                contextMenu.style.visibility = '';
            }

            const viewWidth = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0);
            const viewHeight = Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0);
            const eventRef = domEvent || window.event;
            const clientX = eventRef && Number.isFinite(eventRef.clientX) ? eventRef.clientX : 0;
            const clientY = eventRef && Number.isFinite(eventRef.clientY) ? eventRef.clientY : 0;

            let left = clientX;
            let top = clientY;

            if ((clientX + this.contextMenuDimensions.width + 25) > viewWidth) {
                left = viewWidth - (this.contextMenuDimensions.width + 25);
            }

            if (clientY > viewHeight - this.contextMenuDimensions.height + 25) {
                top = clientY - this.contextMenuDimensions.height;
            }

            contextMenu.style.display = 'block';
            contextMenu.style.position = 'fixed';
            contextMenu.style.left = `${Math.max(0, left)}px`;
            contextMenu.style.top = `${Math.max(0, top)}px`;
            contextMenu.classList.add('show');
        },

        hide_contextMenu() {
            const contextMenu = document.getElementById('context-menu');
            if (!contextMenu) {
                return;
            }

            contextMenu.classList.remove('show');
            contextMenu.style.display = 'none';
        },

        handleDocumentClick(event) {
            const contextMenu = document.getElementById('context-menu');
            if (!contextMenu || !contextMenu.classList.contains('show')) {
                return;
            }

            if (contextMenu.contains(event.target)) {
                return;
            }

            this.hide_contextMenu();
        },

        handleContextMenuKeydown(event) {
            if (event.key === 'Escape') {
                this.hide_contextMenu();
            }
        },

        triggerDownloadSound(sound) {
            if (!sound) {
                return;
            }

            this.hide_contextMenu();
            this.download(sound);
        },

        webShare(sound) {
            if (!sound || !sound.link) {
                return;
            }

            this.hide_contextMenu();

            if (navigator.share) {
                navigator.share({
                    title: `Sound clip: '${sound.sound_transcript || 'Sound'}'`,
                    url: 'https://' + window.location.hostname + sound.link,
                }).then(() => {
                    console.log('Shared.');
                }).catch((error) => {
                    console.error('Error sharing', error);
                });
            }
        },

        copyToClipboard(path) {
            if (!path) {
                return;
            }

            this.hide_contextMenu();

            const getUrl = window.location;
            const baseUrl = getUrl.protocol + '//' + getUrl.host;
            const soundLink = baseUrl + path;
            const textArea = document.createElement('textarea');

            textArea.style.position = 'fixed';
            textArea.style.top = 0;
            textArea.style.left = 0;
            textArea.style.width = '2em';
            textArea.style.height = '2em';
            textArea.style.padding = 0;
            textArea.style.border = 'none';
            textArea.style.outline = 'none';
            textArea.style.boxShadow = 'none';
            textArea.style.background = 'transparent';
            textArea.value = soundLink;

            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();

            try {
                document.execCommand('copy');
            } catch (error) {
                console.log('Unable to copy', error);
            }

            document.body.removeChild(textArea);

            Swal.fire({
                title: 'Link copied',
                text: 'The link has been copied to your clipboard.',
                icon: 'success',
                timer: 1500,
                timerProgressBar: true,
                showConfirmButton: false,
                theme: 'auto',
            });
        },

        addToFavorites(sound) {
            if (!sound || !sound.id) {
                return;
            }

            this.hide_contextMenu();

            if (this.favorites.includes(sound.id)) {
                _.remove(this.favorites, (favorite) => favorite === sound.id);

                axios.delete(`/api/v1/favorites/${sound.id}`)
                    .then(() => {
                        Swal.fire({
                            title: 'Removed from favorites',
                            icon: 'success',
                            timer: 1500,
                            timerProgressBar: true,
                            showConfirmButton: false,
                            theme: 'auto',
                        });
                    })
                    .catch(() => {
                        this.favorites.push(sound.id);
                        Swal.fire({
                            title: 'Problem',
                            text: 'There was a problem with your favorites. Please try again.',
                            icon: 'error',
                            theme: 'auto',
                        });
                    });

                return;
            }

            this.favorites.push(sound.id);

            axios.put(`/api/v1/favorites/${sound.id}`)
                .then(() => {
                    Swal.fire({
                        title: 'Added to favorites',
                        icon: 'success',
                        timer: 1500,
                        timerProgressBar: true,
                        showConfirmButton: false,
                        theme: 'auto',
                    });
                })
                .catch(() => {
                    _.remove(this.favorites, (favorite) => favorite === sound.id);
                    Swal.fire({
                        title: 'Problem',
                        text: 'There was a problem with your favorites. Please try again.',
                        icon: 'error',
                        theme: 'auto',
                    });
                });
        },

        loadFavorites() {
            axios.get('/api/v1/favorites/me')
                .then((response) => {
                    this.favorites = response.data.data || [];
                })
                .catch((error) => {
                    console.log('Unable to load favorites', error);
                });
        },

        report(sound) {
            if (!sound || !sound.id) {
                return;
            }

            this.hide_contextMenu();

            Swal.fire({
                title: 'Report Sound',
                html: 'Are you sure you want to report this sound?<br><br>You should only report sounds with offensive content, personal details, or if this was a Text-to-Speech phrase you created and would like to be removed.',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Report',
                cancelButtonText: 'Cancel',
                theme: 'auto',
            }).then((result) => {
                if (!result.isConfirmed) {
                    return;
                }

                axios.put(`/api/v1/sounds/${sound.id}/reports`)
                    .then(() => {
                        Swal.fire({
                            title: 'Sound Reported',
                            text: 'Sound reported. Our moderators will review it shortly. If you reported a TTS phrase you created, it will be removed momentarily.',
                            icon: 'success',
                            timer: 5000,
                            timerProgressBar: true,
                            theme: 'auto',
                        });
                    })
                    .catch(() => {
                        Swal.fire({
                            title: 'Error',
                            text: 'There was a problem reporting this sound. Please try again.',
                            icon: 'error',
                            timer: 5000,
                            timerProgressBar: true,
                            theme: 'auto',
                        });
                    });
            });
        },

        show_soundModal() {
            this.hide_contextMenu();
        },

        // //Handle global wheel events to scroll the DataTable when it's in view
        // handleGlobalWheel(e) {
        //     //Get the DataTable component via ref
        //     const dt = this.$refs.dataTable;
        //     if (!dt || !dt.getVirtualScrollerRef) return;

        //     //Get the virtual scroller element
        //     const virtualScroller = dt.getVirtualScrollerRef();
        //     if (!virtualScroller || !virtualScroller.$el) return;

        //     //The actual scrollable element is the root $el of the virtual scroller
        //     const scrollEl = virtualScroller.$el;

        //     //Get bounding rect of the scrollable area
        //     const rect = scrollEl.getBoundingClientRect();
        //     const windowHeight = window.innerHeight || document.documentElement.clientHeight;

        //     //Check if the bottom of the datatable is visible in the viewport
        //     const bottomVisible = rect.bottom <= windowHeight && rect.top < windowHeight && rect.bottom > 0;

        //     if (bottomVisible) {
        //         // Check if the datatable can scroll further in the direction of the wheel
        //         const atTop = scrollEl.scrollTop === 0;
        //         const atBottom = Math.abs(scrollEl.scrollTop + scrollEl.clientHeight - scrollEl.scrollHeight) < 2;

        //         if ((e.deltaY < 0 && !atTop) || (e.deltaY > 0 && !atBottom)) {
        //             // DataTable can still scroll - scroll it and prevent page scroll
        //             e.preventDefault();
        //             scrollEl.scrollTop += e.deltaY;
        //             // Reset scroll attempts when actively scrolling table
        //             this.scrollAttempts = 0;
        //         } else if (atTop || atBottom) {
        //             // DataTable is at edge - require multiple attempts before allowing page scroll
        //             if (!this.scrollAttempts) this.scrollAttempts = 0;
        //             this.scrollAttempts++;

        //             // Require 5 scroll attempts before allowing page scroll
        //             if (this.scrollAttempts < 5) {
        //                 e.preventDefault();
        //             } else {
        //                 // Allow page scroll and reset counter
        //                 this.scrollAttempts = 0;
        //             }
        //         }
        //     } else {
        //         // Not over DataTable - reset scroll attempts
        //         this.scrollAttempts = 0;
        //     }
        // },

        //Throttled load
        throttledLoad: _.throttle(function () {
            console.log("Throttled load");
            this.load();
        }, 5000),

        //Subscribe to real-time updates for this user or session's TTS phrases
        subscribe() {
            console.log("Subscribe");

            if (this.subscribed) {
                console.log("Subscribe: Already subscribed");
                return;
            }

            console.log("Subscribe: Not subscribed yet");
            axios.get('/api/v1/tts/me/channel')
                .then(response => {
                    // this.items = response.data;
                    console.log('API call response', response.data);

                    const channel = response.data.data.channel;

                    //Subscribe to channel for real-time updates and reload data when event received
                    //Note that Echo does not support wildcards, so don't even try it
                    if (/.+User\.\d+$/.test(channel)) { //Private channel for user, named session channel for TtsSession
                        const privateChannel = window.Echo.private(channel)
                            .subscribed(() => {
                                console.log('WebSocket connected successfully');
                                this.subscribed = true;
                            })
                            .error((error) => {
                                console.log('WebSocket connection error:', error);
                                this.subscribed = false;
                            });

                        privateChannel
                            .listen('.TtsPhraseUpdated', (e) => {
                                console.log('TtsPhraseUpdated event received', e);
                                console.log(e);

                                //this.throttledLoad();

                                //Update our data from websockets event without reloading
                                const index = this.items.findIndex(item => item.id === e.id);
                                if (index !== -1) {
                                    // Update existing item with new data
                                    this.items[index] = { ...this.items[index], ...e };
                                }

                                // If phrase is completed or failed, emit event sound-finished
                                if (e.completed_at || e.failed_at || e.flagged) {
                                    console.log('Phrase completed/failed/flagged, emitting phrase-finished event', e);
                                    this.$emit('phrase-finished', e);
                                }

                                //Failed
                                if (e.failed_at && !e.flagged) {
                                    Swal.fire({
                                        title: 'Problem',
                                        text: 'There was a problem generating your phrase "' + e.phrase_text + '". Use Manage My Phrases to review it.',
                                        icon: 'error',
                                        buttons: {
                                            manage: "Manage My Phrases",
                                            OK: true
                                        },
                                        theme: 'auto'
                                    }).then((value) => {
                                        if (value === "manage") {
                                            window.location.href = '/tts/me';
                                        }
                                    });
                                    return true;
                                }

                                //Flagged
                                if (e.flagged) {
                                    Swal.fire({
                                        title: 'Flagged',
                                        text: 'Your TTS phrase "' + e.phrase_text + '" was flagged by AI moderation. We want this site to be fun for everyone. Please avoid hateful or harmful content. Use Manage My Phrases to review it.',
                                        icon: 'warning',
                                        buttons: {
                                            manage: "Manage My Phrases",
                                            OK: true
                                        },
                                        theme: 'auto'
                                    }).then((value) => {
                                        if (value === "manage") {
                                            window.location.href = '/tts/me';
                                        }
                                    });
                                    return true;
                                }
                            })
                            .listen('.TtsPhraseCreated', (e) => {
                                console.log('TtsPhraseCreated event received');
                                console.log(e);
                                this.throttledLoad();
                            })
                            .listen('.TtsPhraseDeleted', (e) => {
                                console.log('TtsPhraseDeleted event received');
                                console.log(e);

                                //Remove from phrases array
                                const index = this.items.findIndex(item => item.id === e.id);
                                if (index !== -1) {
                                    this.items.splice(index, 1);
                                    this.totalRecords--;
                                }
                            });
                    } else {
                        const publicChannel = window.Echo.channel(channel) //Session channel for guests
                            .subscribed(() => {
                                console.log('WebSocket connected successfully (public channel)');
                                this.subscribed = true;
                            })
                            .error((error) => {
                                console.log('WebSocket connection error (public channel):', error);
                                this.subscribed = false;
                            });

                        publicChannel
                            .listen('.TtsPhraseUpdated', (e) => {
                                console.log('TtsPhraseUpdated event received');
                                console.log(e);

                                //this.throttledLoad();

                                //Update our data from websockets event without reloading
                                const index = this.items.findIndex(item => item.id === e.id);
                                if (index !== -1) {
                                    // Update existing item with new data
                                    this.items[index] = { ...this.items[index], ...e };
                                }

                                //Failed
                                if (e.failed_at && !e.flagged) {
                                    Swal.fire({
                                        title: 'Problem',
                                        text: 'There was a problem generating your phrase "' + e.phrase_text + '". Use Manage My Phrases to review it.',
                                        icon: 'error',
                                        buttons: {
                                            manage: "Manage My Phrases",
                                            OK: true
                                        },
                                        theme: 'auto'
                                    }).then((value) => {
                                        if (value === "manage") {
                                            window.location.href = '/tts/me';
                                        }
                                    });
                                    return true;
                                }

                                //Flagged
                                if (e.flagged) {
                                    Swal.fire({
                                        title: 'Flagged',
                                        text: 'Your TTS phrase "' + e.phrase_text + '" was flagged by AI moderation. We want this site to be fun for everyone. Please avoid hateful or harmful content. Use Manage My Phrases to review it.',
                                        icon: 'warning',
                                        buttons: {
                                            manage: "Manage My Phrases",
                                            OK: true
                                        },
                                        theme: 'auto'
                                    }).then((value) => {
                                        if (value === "manage") {
                                            window.location.href = '/tts/me';
                                        }
                                    });
                                    return true;
                                }
                            })
                            .listen('.TtsPhraseCreated', (e) => {
                                console.log('TtsPhraseCreated event received');
                                console.log(e);
                                this.throttledLoad();
                            })
                            .listen('.TtsPhraseDeleted', (e) => {
                                console.log('TtsPhraseDeleted event received');
                                console.log(e);

                                //Remove from items array
                                const index = this.items.findIndex(item => item.id === e.id);
                                if (index !== -1) {
                                    this.items.splice(index, 1);
                                    this.totalRecords--;
                                } else {
                                    // If not found in current page, just update total count
                                    this.totalRecords = Math.max(0, this.totalRecords - 1);
                                }
                            });
                    }
                })
                .catch(error => {
                    console.log('API call error', error);
                    this.subscribed = false;
                    this.error = true;
                });
        },
        delete: function (item) {
            console.log("Delete item", item);

            this.loading = true;

            return axios.delete('/api/v1/tts/phrases/' + item.id)
                .then(response => {
                    console.log('API call response', response.data);
                    this.throttledLoad();
                    return response;
                })
                .catch(error => {
                    console.log('API call error', error);
                    this.error = true;
                    throw error;
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        retry: function (item) {
            console.log("Retry item", item);
            this.loading = true;

            axios.put('/api/v1/tts/phrases/' + item.id + '/retry')
                .then(response => {
                    console.log('API call response', response.data);
                    this.throttledLoad();
                })
                .catch(error => {
                    console.log('API call error', error);

                    //Show error message
                    Swal.fire({
                        title: 'Error',
                        text: 'An error occurred while retrying the phrase: ' + (error.response && error.response.data && error.response.data.message ? error.response.data.message : error.message),
                        icon: 'error',
                        theme: 'auto'
                    });
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        deleteSelection: function () {
            console.log("Delete selection", this.selection);

            Swal.fire({
                title: 'Are you sure?',
                text: 'You are about to delete ' + this.selection.length + ' selected phrase' + (this.selection.length > 1 ? 's' : '') + ' and sound' + (this.selection.length > 1 ? 's' : '') + '. This action cannot be undone.',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Yes, delete them',
                cancelButtonText: 'No, keep them',
                theme: 'auto'
            }).then((result) => {
                if (result.isConfirmed) {
                    const deletePromises = this.selection.map(item => this.delete(item));
                    Promise.allSettled(deletePromises).then(results => {
                        const hasError = results.some(r => r.status === 'rejected');
                        if (hasError) {
                            Swal.fire({
                                title: 'Some errors occurred',
                                text: 'Some phrases or sounds could not be deleted.',
                                icon: 'error',
                                theme: 'auto'
                            });
                        } else {
                            Swal.fire({
                                title: 'Deleted',
                                text: 'Your selected phrases and sounds have been deleted.',
                                icon: 'success',
                                theme: 'auto'
                            });
                        }
                        this.selection = null;
                    });
                }
            });
        },
        play: function (sound) {
            console.log("Play sound", sound);
            this.audio.pause();
            this.audio.src = sound.sound_file_url;
            this.audio.play().then(() => { console.log('Sound played') }).catch((error) => { console.log('Sound play error', error) })
        },
        edit: function (phrase) {
            console.log("Edit phrase", phrase);
            //Emit event to parent
            this.$emit('edit-phrase', phrase);
        },
        download: function (sound) {
            console.log("Download sound", sound);
            // Swal.fire({
            //     title: 'Download Sound',
            //     text: 'The sound page will open for your download',
            //     icon: 'info',
            //     showCancelButton: true,
            //     confirmButtonText: 'OK',
            //     cancelButtonText: 'Cancel'
            // }).then((result) => {
            //     if (result.isConfirmed) {
            //         window.open(sound.link + '?download', '_blank');
            //     }
            // });

            //Use SoundDownloader component to download sound
            if (this.$refs.soundDownloader) {
                this.$refs.soundDownloader.download(sound);
            } else {
                console.error('SoundPlayer: No soundDownloader ref');
            }
        },
        requestWebPushPermission: async function () {
            try {
                const permission = await requestWebPushPermission();
                if (permission === 'granted') {
                    Swal.fire('Notifications Enabled', 'You will receive notifications when your TTS phrases are ready.', 'success');
                } else if (permission === 'denied') {
                    Swal.fire('Notifications Denied', 'You have denied notification permissions. You can enable them in your browser settings for this site.', 'error');
                } else {
                    Swal.fire('Notifications Not Enabled', 'You have dismissed the notification permission request.', 'info');
                }
            } catch (error) {
                console.error('Error requesting notification permission:', error);
                Swal.fire('Error', 'An error occurred while requesting notification permission: ' + error, 'error');

                window.sentryCaptureMessage('Error requesting notification permission: ' + error);
            }
        },
        removeSubscription: async function () {
            try {
                await removeSubscription();
                Swal.fire('Subscription Removed', 'You will no longer receive notifications.', 'success');
            } catch (error) {
                console.error('Error removing subscription:', error);
                Swal.fire('Error', 'An error occurred while removing the subscription.', 'error');

                window.sentryCaptureMessage('Error removing subscription: ' + error);
            }
        },
        requestTestNotification: async function () {
            try {
                await requestTestNotification();
                Swal.fire('Test Notification Sent', 'A test notification will be sent shortly.', 'success');
            } catch (error) {
                console.error('Error sending test notification:', error);
                Swal.fire('Error', 'An error occurred while sending the test notification.', 'error');

                window.sentryCaptureMessage('Error sending test notification: ' + error);
            }
        },
    },
    watch: {
        filters: {
            handler: _.debounce(function () {
                console.log('Filters changed', this.filters);
                this.page = 1; //Reset to first page on new search
                this.load();
            }, 400), //Wait 400ms after typing stops
            deep: true,
        },
    },
    created: function () {
        console.log("created MyTTSPhrases");
    },
    destroyed: function () {
        console.log("MyTTSPhrases destroyed");
    },
    beforeDestroy: function () {
        console.log("MyTTSPhrases beforeDestroy");
    },
    updated: function () {
        console.log("MyTTSPhrases updated");
    },
};
</script>

<style scoped>
.tts-phrase-cell {
    width: 250px;
    height: 46px;
}

/* Badges shrink text by default; restore to 1em for status legibility */
.my-tts-status .badge,
.my-tts-status>.btn {
    font-size: 1em;
}

.btn-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 31px;
    height: 31px;
    padding: 0;
}
</style>

<style>
.p-datatable {
}

/* Orange scrollbars */
.p-virtualscroller {
    scrollbar-color: #e86115 #f2f2f2;
    scrollbar-width: auto;
}

.p-datatable-wrapper {
    overflow: scroll !important;
}

.p-datatable-scrollable-body {
    overflow: scroll !important;
}

/* Focus state for the entire input group - border color only, no shadow */
.input-group:focus-within .input-group-text {
    border-color: #80bdff;
}

.input-group:focus-within .btn-outline-secondary {
    border-color: #80bdff;
}

/* Ensure the focus shadow doesn't duplicate and remove default shadow */
.input-group .form-control:focus {
    box-shadow: none;
    border-color: #80bdff;
}

/* Make sure the borders connect properly on focus */
.input-group:focus-within .input-group-prepend .input-group-text {
    border-right-color: #80bdff;
}

.input-group:focus-within .form-control.border-end-0+.input-group-append .btn {
    border-left-color: #80bdff;
}

/* Remove any default focus shadows from buttons */
.input-group .btn:focus {
    box-shadow: none;
}

/* Make clear search icon visible on hover */
.input-group-append .btn.btn-outline-secondary:hover .fa-times {
    color: #e86115 !important;
    /* Use custom orange for visibility */
}

/* Disable hover effects for buttons with btn-no-hover class */
.btn-no-hover:hover {
    background-color: transparent !important;
    border-color: inherit !important;
    color: inherit !important;
    box-shadow: none !important;
    transform: none !important;
}

/* Specific override for btn-outline-dark to maintain its original appearance */
.btn-outline-dark.btn-no-hover:hover {
    background-color: transparent !important;
    border-color: #343a40 !important;
    color: #343a40 !important;
}

@media (prefers-color-scheme: dark) {
    .btn-outline-dark.btn-no-hover:hover {
        background-color: transparent !important;
        border-color: #6c757d !important;
        color: #6c757d !important;
    }
}

.p-datatable.p-datatable-sm .p-datatable-tbody>tr>td {
    padding: 0.25rem 0.5rem !important;
    border-width: 0 0 1px 0 !important;
}

.p-datatable.p-datatable-sm .p-datatable-thead>tr>th {
    padding: 0.35rem 0.5rem !important;
}

/* Make the full PrimeVue DataTable transparent */
.p-datatable,
.p-datatable-table-container,
.p-datatable-table,
.p-datatable-header,
.p-datatable-footer,
.p-datatable-thead > tr > th,
.p-datatable-tbody > tr,
.p-datatable-tbody > tr > td,
.p-datatable-emptymessage,
.p-paginator {
    background: #ffffff20 !important;
}
</style>
